import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Button } from "@dl/web-core-js/ui";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BORDER_TYPES, OUTPUT_TYPES, useApi, type PrintSettingsDTO } from "../../api";
import { crudEditor, numberField, selectField } from "../../components/editor";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";

/**
 * Print settings profiles: how a production image is laid out before it goes
 * to a printer. Opening a row shows the product × centre lines that resolve to
 * this profile.
 */
export function PrintSettingsPage() {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const allowed = useAllowed(LogicView.CONFIG_PRINT_SETTINGS);
  const title = t("navigation.item.print-settings", "Print settings");

  const { data, isLoading, error } = useQuery({
    queryKey: ["print-settings"],
    queryFn: () => api.printSettings.findAll(),
    enabled: allowed,
  });

  const rows = data ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["print-settings"] });

  const cols = useMemo(() => {
    const c = defineColumns<PrintSettingsDTO>();
    return [
      c.text("border_type", (row) => row.border_type, {
        headerKey: "column.border-type",
        filter: "select",
        priority: 1,
      }),
      c.number("h_border_in_inches", (row) => row.h_border_in_inches, {
        headerKey: "column.h_border-in-inches",
        priority: 3,
      }),
      c.number("v_border_in_inches", (row) => row.v_border_in_inches, {
        headerKey: "column.v-border-in-inches",
        priority: 3,
      }),
      c.number("h_white_space_in_inches", (row) => row.h_white_space_in_inches, {
        headerKey: "column.h-white-space-in-inches",
        priority: 4,
      }),
      c.number("v_white_space_in_inches", (row) => row.v_white_space_in_inches, {
        headerKey: "column.v-white-space-in-inches",
        priority: 4,
      }),
      c.number("rotation", (row) => row.rotation, { headerKey: "column.rotation", priority: 4 }),
      c.text("output_type", (row) => row.output_type, {
        headerKey: "column.output-type",
        filter: "select",
        priority: 2,
      }),
      c.number("maximum_file_size_mb", (row) => row.maximum_file_size_mb, {
        headerKey: "column.maximum-file-size",
        priority: 5,
      }),
    ];
  }, []);

  const editor = useMemo(
    () =>
      crudEditor<PrintSettingsDTO>({
        client: api.printSettings,
        refresh,
        t,
        fields: {
          border_type: selectField(
            (row) => row.border_type,
            (row, value) => ({ ...row, border_type: value as PrintSettingsDTO["border_type"] }),
            BORDER_TYPES,
          ),
          h_border_in_inches: numberField(
            (row) => row.h_border_in_inches,
            (row, value) => ({ ...row, h_border_in_inches: Number(value) }),
            true,
          ),
          v_border_in_inches: numberField(
            (row) => row.v_border_in_inches,
            (row, value) => ({ ...row, v_border_in_inches: Number(value) }),
            true,
          ),
          h_white_space_in_inches: numberField(
            (row) => row.h_white_space_in_inches,
            (row, value) => ({ ...row, h_white_space_in_inches: Number(value) }),
            true,
          ),
          v_white_space_in_inches: numberField(
            (row) => row.v_white_space_in_inches,
            (row, value) => ({ ...row, v_white_space_in_inches: Number(value) }),
            true,
          ),
          rotation: numberField(
            (row) => row.rotation,
            (row, value) => ({ ...row, rotation: Number(value) }),
            true,
          ),
          output_type: selectField(
            (row) => row.output_type,
            (row, value) => ({ ...row, output_type: value as PrintSettingsDTO["output_type"] }),
            OUTPUT_TYPES,
          ),
          maximum_file_size_mb: numberField(
            (row) => row.maximum_file_size_mb,
            (row, value) => ({
              ...row,
              maximum_file_size_mb: value === "" ? null : Number(value),
            }),
          ),
        },
        newRow: () => ({
          id: 0,
          border_type: "NONE",
          h_border_in_inches: 0,
          v_border_in_inches: 0,
          h_white_space_in_inches: 0,
          v_white_space_in_inches: 0,
          rotation: 0,
          output_type: "PRESERVE_FORMAT",
          maximum_file_size_mb: null,
        }),
        validate: (row) => {
          const errors: Record<string, string> = {};
          const invalid = t("form.validation.field.greater-than", "Value must be greater than {{min}}", {
            min: 0,
          });
          (
            [
              "h_border_in_inches",
              "v_border_in_inches",
              "h_white_space_in_inches",
              "v_white_space_in_inches",
              "rotation",
            ] as const
          ).forEach((key) => {
            const value = row[key];
            if (value === null || value === undefined || Number.isNaN(value) || value < 0) {
              errors[key] = invalid;
            }
          });
          return Object.keys(errors).length > 0 ? errors : null;
        },
      }),
    [api, t],
  );

  if (!allowed) return <AccessDenied title={title} />;
  if (error) return <ErrorState title={title} error={error} />;

  return (
    <>
      <PageHeader title={title} />
      <DataTable<PrintSettingsDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        stateKey="/config/print-settings"
        loading={isLoading}
        editor={editor}
        onRowClick={(row) =>
          void navigate({ to: "/config/print-settings/$id", params: { id: String(row.id) } })
        }
        rowActions={(row) => (
          <Button
            size="sm"
            variant="ghost"
            title={t("tab.lines", "Lines")}
            className="size-7 p-0"
            icon={<Eye className="size-3.5" />}
            onClick={() =>
              void navigate({ to: "/config/print-settings/$id", params: { id: String(row.id) } })
            }
          />
        )}
      />
    </>
  );
}
